import React from 'react';
import { LanguageGroup, NewbornRankingData, Sex, SexSelection } from 'lib/Types';
import "./Ranking.css";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

import info from 'data/info.json';
import translationsJson from 'data/translations.json';

import Slider from '@mui/material/Slider';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import LanguageFilter from 'components/LanguageFilter/LanguageFilter';
import { Mark } from '@mui/base';
import GenderFilter from 'components/GenderFilter/GenderFilter';
import IconButton from '@mui/material/IconButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { MobileDeviceContext } from 'lib/Screen';

am4core.useTheme(am4themes_animated);

const translations: Translations = translationsJson;

interface ChartComponents {
    readonly elementName: string;
    chart: am4charts.Chart | null;
    label: am4core.Label | null;
    series: am4charts.Series | null;
    valueAxis: am4charts.ValueAxis | null;
    categoryAxis: am4charts.CategoryAxis | null;
    readonly sex: Sex;
    readonly color: string;
    allNames: Set<string>;
}

interface DataItem {
    name: string;
    count: number;
}

interface Props {
    title: string;
    names: NewbornRankingData;
    //show: boolean;
    nameSelected?(name: string, sex: Sex): void;
}

interface State {
    interval: NodeJS.Timer | null;
    sexSelection: SexSelection;
    language: LanguageGroup;
    currentYear: string;
    isPlaying: boolean
}

class AnimatedRanking extends React.Component<Props, State> {
    static contextType = MobileDeviceContext;
    static stepDuration = 4000;
    static transitionDuration = (AnimatedRanking.stepDuration * 2) / 3;

    private chartComponents: ChartComponents[];
    private years: string[];
    private yearIndex: number;
    private sliderMarks: Mark[];
    private interval: NodeJS.Timer | null = null;
    private wasPlaying: boolean;

    constructor(props: Props) {
        super(props);

        this.chartComponents = [
            {
                elementName: "rankingChartDivMale",
                chart: null,
                label: null,
                series: null,
                valueAxis: null,
                categoryAxis: null,
                sex: Sex.Male,
                color: "#19486a",
                allNames: new Set<string>(),
            },
            {
                elementName: "rankingChartDivFemale",
                chart: null,
                label: null,
                series: null,
                valueAxis: null,
                categoryAxis: null,
                sex: Sex.Female,
                color: "#c5192d",
                allNames: new Set<string>(),
            }
        ];

        this.years = Object.keys(this.props.names).sort();
        this.yearIndex = this.years.length - 1;
        this.sliderMarks = [];
        this.wasPlaying = false;

        this.state = {
            interval: null,
            sexSelection: SexSelection.All,
            language: LanguageGroup.All,
            currentYear: this.years[this.yearIndex],
            isPlaying: false
        }

        for (let year of this.years) {
            this.props.names[year][LanguageGroup.All].m.slice(0, info.newbornNameRankingCount).forEach(item => this.chartComponents[0].allNames.add(item.name));
            this.props.names[year][LanguageGroup.All].f.slice(0, info.newbornNameRankingCount).forEach(item => this.chartComponents[1].allNames.add(item.name));
        }

        //console.log("male names:", this.chartComponents[0].allNames.size);
        //console.log("female names:", this.chartComponents[1].allNames.size);

        this.onChangeSexSelection = this.onChangeSexSelection.bind(this);
        this.onChangeLanguage = this.onChangeLanguage.bind(this);
        this.onChangeSlider = this.onChangeSlider.bind(this);
        this.onChangeSelect = this.onChangeSelect.bind(this);
    }

    private createChart(chartComponents: ChartComponents, showLabel: boolean) {
        let chart = am4core.create(chartComponents.elementName, am4charts.XYChart);
        chart.padding(40, 40, 40, 0);
        //chart.svgContainer!.autoResize = false;

        let label = chart.plotContainer.createChild(am4core.Label);
        label.x = am4core.percent(97);
        label.y = am4core.percent(95);
        label.horizontalCenter = "right";
        label.verticalCenter = "middle";
        label.dx = -15;
        label.fontSize = 50;
        label.setVisibility(showLabel);

        let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.dataFields.category = "name";
        categoryAxis.renderer.minGridDistance = 1;
        categoryAxis.renderer.inversed = true;
        categoryAxis.renderer.grid.template.disabled = true;
        categoryAxis.renderer.labels.template.interactionsEnabled = true;
        categoryAxis.renderer.labels.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;

        categoryAxis.renderer.labels.template.events.on("hit", (event) => {
            const dataItem = (event.target.dataItem?.dataContext as DataItem);
            //console.log("clicked on:", dataItem);

            if (this.props.nameSelected)
                this.props.nameSelected(dataItem.name, chartComponents.sex);
        });

        let valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.rangeChangeEasing = am4core.ease.linear;
        valueAxis.rangeChangeDuration = AnimatedRanking.transitionDuration;
        valueAxis.extraMax = 0.1;
        valueAxis.strictMinMax = true;

        let series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.categoryY = "name";
        series.dataFields.valueX = "count";
        series.tooltipText = "{valueX.value}"
        series.columns.template.strokeOpacity = 0;
        series.columns.template.column.cornerRadiusBottomRight = 5;
        series.columns.template.column.cornerRadiusTopRight = 5;
        series.interpolationDuration = AnimatedRanking.transitionDuration;
        series.interpolationEasing = am4core.ease.linear;
        series.fill = am4core.color(chartComponents.color);
        series.fillOpacity = 1;
        series.strokeOpacity = 1;
        series.columns.template.interactionsEnabled = true;
        series.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;

        series.columns.template.events.on("hit", (event) => {
            const dataItem = (event.target.dataItem?.dataContext as DataItem);
            //console.log("clicked on:", dataItem);

            if (this.props.nameSelected)
                this.props.nameSelected(dataItem.name, chartComponents.sex);
        });

        let labelBullet = series.bullets.push(new am4charts.LabelBullet())
        labelBullet.label.horizontalCenter = "right";
        labelBullet.label.text = "{values.valueX.workingValue.formatNumber('#.')}";
        labelBullet.label.textAlign = "end";
        labelBullet.label.dx = -10;
        labelBullet.label.dy = 1;
        labelBullet.label.fill = am4core.color("#ffffff");

        chart.zoomOutButton.disabled = true;

        const year = this.state.currentYear;
        label.text = year;

        categoryAxis.sortBySeries = series;

        this.setChartData(chart, categoryAxis, year, this.state.language, chartComponents.sex, chartComponents.allNames, false);
        //console.log("chart init data:", chart.data);

        chartComponents.chart = chart;
        chartComponents.label = label;
        chartComponents.series = series;
        chartComponents.valueAxis = valueAxis;
        chartComponents.categoryAxis = categoryAxis;
    }

    private setChartData(chart: am4charts.Chart, categoryAxis: am4charts.CategoryAxis, year: string, language: LanguageGroup, sex: Sex, names: Set<string>, newData: boolean) {
        const data: any[] = [];
        const subset = this.props.names[year][language][sex].slice(0, Math.min(this.props.names[year][language][sex].length, info.newbornNameRankingCount));
        for (let name of names) {
            const entry = subset.find(e => e.name === name);
            if (entry)
                data.push({ name: name, count: entry.count });
            else
                data.push({ name: name, count: 0 });
        }

        chart.data = data;
        if (newData)
            chart.invalidateData();
        categoryAxis.zoom({ start: 0, end: subset.length / chart.data.length });
    }

    private play(year?: string) {
        this.interval = setInterval(() => this.nextYear(), AnimatedRanking.stepDuration)
        this.nextYear(year);
        this.setState({ isPlaying: true });
    }

    private stop() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
        this.setState({ isPlaying: false });
    }

    private nextYear(year?: string) {
        let yearIndex = -1;
        if (year !== undefined)
            yearIndex = this.years.findIndex(v => v === year);
        if (yearIndex === -1)
            yearIndex = this.yearIndex + 1;
        if (yearIndex >= this.years.length)
            yearIndex = 0;

        for (let chartComponents of this.chartComponents) {
            if (chartComponents.chart === null)
                continue;

            //console.log("year advanced to:", this.years[yearIndex]);
            //console.log("current data:", chartComponents.chart.data);

            const data = this.props.names[this.years[yearIndex]][this.state.language][chartComponents.sex];
            const subSet = data.slice(0, Math.min(data.length, info.newbornNameRankingCount));
            for (var i = 0; i < chartComponents.chart.data.length; i++) {
                const name = chartComponents.chart.data[i].name;
                const entry = subSet.find(e => e.name === name);
                if (entry) {
                    chartComponents.chart.data[i].count = entry.count;
                } else {
                    chartComponents.chart.data[i].count = 0;
                }
            }

            //console.log("new chart data:", chartComponents.chart.data);

            if (yearIndex === 0 || year !== undefined) {
                chartComponents.series!.interpolationDuration = AnimatedRanking.transitionDuration / 2;
                chartComponents.valueAxis!.rangeChangeDuration = AnimatedRanking.transitionDuration / 2;
            } else {
                chartComponents.series!.interpolationDuration = AnimatedRanking.transitionDuration;
                chartComponents.valueAxis!.rangeChangeDuration = AnimatedRanking.transitionDuration;
            }

            chartComponents.chart.invalidateRawData();
            chartComponents.label!.text = this.years[yearIndex];
            chartComponents.categoryAxis!.zoom({ start: 0, end: subSet.length / chartComponents.chart.data.length });
        }

        //this.categoryAxis!.zoom({ start: 0, end: info.newbornNameRankingCount / this.categoryAxis!.dataItems.length });
        this.yearIndex = yearIndex;
        this.setState({ currentYear: this.years[yearIndex] });
    }

    private onChangeSexSelection(sexSelection: SexSelection) {
        //console.log("sex change to:", sexSelection);
        this.wasPlaying = this.state.isPlaying;
        this.stop();
        this.setState({ sexSelection: sexSelection });
    }

    private onChangeLanguage(language: LanguageGroup) {
        //console.log("language change to:", language);
        const isPlaying = this.state.isPlaying;
        this.stop();

        this.chartComponents[0].allNames.clear();
        this.chartComponents[1].allNames.clear();

        for (let year of this.years) {
            this.props.names[year][language].m.slice(0, info.newbornNameRankingCount).forEach(item => this.chartComponents[0].allNames.add(item.name));
            this.props.names[year][language].f.slice(0, info.newbornNameRankingCount).forEach(item => this.chartComponents[1].allNames.add(item.name));
        }

        //console.log("male names:", this.chartComponents[0].allNames.size);
        //console.log("female names:", this.chartComponents[1].allNames.size);

        for (let cc of this.chartComponents) {
            if (cc.chart)
                this.setChartData(cc.chart, cc.categoryAxis!, this.state.currentYear, language, cc.sex, cc.allNames, true);
        }

        this.setState({ language: language });

        if (isPlaying)
            this.play(this.state.currentYear);
    }

    private onChangeSlider(value: number | number[], committed: boolean) {
        //console.log("year slider change:", value);
        const year = String(typeof value === "number" ? value : value[0]);
        this.setState({ currentYear: year });
        if (committed)
            this.updateInterval(year);
    }

    private onChangeSelect(event: SelectChangeEvent<number>, _: any) {
        //console.log("year select change:", event.target.value);
        const year = String(event.target.value);
        this.setState({ currentYear: year });
        this.updateInterval(year);
    }

    private updateInterval(year: string) {
        const isPlaying = this.state.isPlaying;
        this.stop();
        if (isPlaying)
            this.play(year);
        else
            this.nextYear(year);
    }

    componentDidMount() {
        for (let i = 0; i < this.chartComponents.length; i++) {
            let chartComponents = this.chartComponents[i];
            if (!chartComponents.chart)
                this.createChart(chartComponents, i === this.chartComponents.length - 1);
        }
    }

    componentWillUnmount() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }

        for (let chartComponents of this.chartComponents) {
            if (chartComponents.chart) {
                chartComponents.chart.dispose();
                chartComponents.chart = null;
            }
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        /*
        if (prevProps.show !== this.props.show && this.props.show) {
            for (let chartComponents of this.chartComponents) {
                if (!chartComponents.chart)
                    this.createChart(chartComponents);
            }
        }
        */

        if (prevState.sexSelection !== this.state.sexSelection) {
            const sexSelection = this.state.sexSelection;
            if (sexSelection === SexSelection.All) {
                for (let i = 0; i < this.chartComponents.length; i++) {
                    let chartComponents = this.chartComponents[i];
                    if (!chartComponents.chart) {
                        this.createChart(chartComponents, i === this.chartComponents.length - 1);
                    } else {
                        chartComponents.label?.setVisibility(i === this.chartComponents.length - 1);
                    }
                }
            } else if (sexSelection === SexSelection.Male) {
                for (let chartComponents of this.chartComponents) {
                    if (chartComponents.sex === Sex.Female && chartComponents.chart) {
                        chartComponents.chart.dispose();
                        chartComponents.chart = null;
                    } else if (chartComponents.sex === Sex.Male) {
                        if (!chartComponents.chart) {
                            this.createChart(chartComponents, true);
                        } else {
                            chartComponents.label?.setVisibility(true);
                        }
                    }
                }
            } else if (sexSelection === SexSelection.Female) {
                for (let chartComponents of this.chartComponents) {
                    if (chartComponents.sex === Sex.Male && chartComponents.chart) {
                        chartComponents.chart.dispose();
                        chartComponents.chart = null;
                    } else if (chartComponents.sex === Sex.Female) {
                        if (!chartComponents.chart) {
                            this.createChart(chartComponents, true);
                        } else {
                            chartComponents.label?.setVisibility(true);
                        }
                    }
                }
            }

            if (this.wasPlaying)
                this.play(this.state.currentYear);
        }
    }

    render() {
        if(this.sliderMarks.length === 0) {
            if (this.context) {
                let middleValue = Number(this.years[0]) + (Number(this.years[this.yearIndex]) - Number(this.years[0])) / 2
                middleValue = Math.round(middleValue / 10) * 10;
                this.sliderMarks = [{ value: Number(this.years[0]), label: this.years[0] }, { value: Number(middleValue), label: middleValue }, { value: Number(this.years[this.yearIndex]), label: this.years[this.yearIndex] }];
            } else {
                this.sliderMarks = this.years.filter((v, i) => i % 10 === 0).map(y => ({ value: Number(y), label: y }));
            }
        }
        const optionList = this.years.map((value) => <MenuItem value={value} key={`year-${value}`}>{value}</MenuItem>);
        const widthMale = this.state.sexSelection === SexSelection.All ? "50%" : (this.state.sexSelection === SexSelection.Male ? "100%" : "0%");
        const widthFemale = this.state.sexSelection === SexSelection.All ? "50%" : (this.state.sexSelection === SexSelection.Female ? "100%" : "0%");

        return (
            <div className='animated-ranking-container'>
                <div className='animated-ranking-filter'>
                    <GenderFilter onUpdate={this.onChangeSexSelection} young={true} />
                    <LanguageFilter onUpdate={this.onChangeLanguage} />

                    <div className='year-controls'>
                        <Slider value={Number(this.state.currentYear)} min={Number(this.years[0])} max={Number(this.years.at(-1))}
                            aria-label="Year slider" valueLabelDisplay="auto" className="slider" onChange={(_, value) => this.onChangeSlider(value, false)}
                            onChangeCommitted={(_, value) => this.onChangeSlider(value, true)} marks={this.sliderMarks} />

                        <IconButton color="primary" className="button" onClick={() => this.state.isPlaying ? this.stop() : this.play()}>
                            {this.state.isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                        </IconButton>
                    </div>

                    <FormControl className="dropdown">
                        <InputLabel id="dropdown-label">{translations.year[window.lang]}</InputLabel>
                        <Select labelId="dropdown-label" label={translations.year[window.lang]} onChange={this.onChangeSelect} value={Number(this.state.currentYear)}>
                            {optionList}
                        </Select>
                    </FormControl>
                </div>
                <div className='animated-ranking-charts'>
                    <div id="rankingChartDivMale" style={{ width: widthMale, height: "600px" }}></div>
                    <div id="rankingChartDivFemale" style={{ width: widthFemale, height: "600px" }}></div>
                </div>
            </div>
        );
    }
}

export default AnimatedRanking;